import React, { useState } from 'react';
import './AlertPopup.css';
import { dismissAlertTemporarily } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';

const AlertPopup = ({ alert, onClose, onDismissed }) => {
  const [dismissing, setDismissing] = useState(false);
  const [dismissType, setDismissType] = useState(null);
  const [dismissHours, setDismissHours] = useState(24);

  if (!alert) return null;

  const handleDismiss = async (type) => {
    setDismissing(true);
    setDismissType(type);
    
    try {
      if (type === 'temporary') {
        await dismissAlertTemporarily(alert.id, dismissHours);
      }
      
      // Call the dismiss handler which will refresh alerts
      if (onDismissed) {
        await onDismissed(alert.id, type);
      }
      onClose();
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
      alert('Failed to dismiss alert. Please try again.');
    } finally {
      setDismissing(false);
      setDismissType(null);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return formatDateTime(timestamp);
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="alert-popup-overlay" onClick={onClose}>
      <div className="alert-popup" onClick={(e) => e.stopPropagation()}>
        <div className="alert-popup-header">
          <h2 className="alert-popup-title">Alert Details</h2>
          <button className="alert-popup-close" onClick={onClose}>✕</button>
        </div>

        <div className="alert-popup-content">
          <div className="alert-popup-main">
            <div className="alert-popup-severity" style={{ borderLeftColor: getSeverityColor(alert.severity) }}>
              <span className="alert-popup-severity-badge" style={{ backgroundColor: getSeverityColor(alert.severity) }}>
                {alert.severity.toUpperCase()}
              </span>
            </div>
            
            <div className="alert-popup-details">
              <div className="alert-popup-field">
                <label>Message:</label>
                <div className="alert-popup-value">{alert.message}</div>
              </div>

              {alert.siteName && (
                <div className="alert-popup-field">
                  <label>Site:</label>
                  <div className="alert-popup-value">{alert.siteName}</div>
                </div>
              )}

              {alert.siteIp && (
                <div className="alert-popup-field">
                  <label>Site IP:</label>
                  <div className="alert-popup-value">{alert.siteIp}</div>
                </div>
              )}

              <div className="alert-popup-field">
                <label>Type:</label>
                <div className="alert-popup-value">{alert.type || 'N/A'}</div>
              </div>

              <div className="alert-popup-field">
                <label>Timestamp:</label>
                <div className="alert-popup-value">{formatTimestamp(alert.timestamp)}</div>
              </div>
            </div>
          </div>

          <div className="alert-popup-actions">
            <h3 className="alert-popup-actions-title">Dismiss Alert</h3>
            
            <div className="alert-popup-dismiss-options">
              <div className="dismiss-option">
                <div className="dismiss-temporary-controls">
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={dismissHours}
                    onChange={(e) => setDismissHours(parseInt(e.target.value) || 24)}
                    className="dismiss-hours-input"
                    disabled={dismissing}
                  />
                  <span className="dismiss-hours-label">hours</span>
                  <button
                    className="dismiss-btn dismiss-temporary"
                    onClick={() => handleDismiss('temporary')}
                    disabled={dismissing}
                  >
                    {dismissing && dismissType === 'temporary' ? 'Stopping...' : 'Stop alert temporarily'}
                  </button>
                </div>
                <span className="dismiss-description">This alert will be ignored for the selected period. After this period, it will be created again if the condition still exists.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertPopup;

